<div class="content-info st-page">

  <!-- ── Sticky document header ───────────────────────────────────────── -->
  <div class="zf-doc-head">
    <div class="zf-doc-left">
      <a class="zf-doc-back" [routerLink]="['/stocktake']"><i class="bi bi-arrow-left"></i> Stock takes</a>
      <div class="zf-doc-title-row">
        <h2 class="zf-doc-title">{{ title }}</h2>
        @if (storeContextLabel) {
        <span class="zf-chip" title="Store this stock take is being done for">
          <i class="bi bi-shop"></i>{{ storeContextLabel }}
        </span>
        }
        @if (stockTakeModeLabel) {
        <span class="zf-chip" [class.warn]="stockTakeModeLabel !== 'Full'"
          title="Whether this is a full or partial stock take">
          <i class="bi bi-clipboard-check"></i>{{ stockTakeModeLabel }} stock take
        </span>
        }
        @if (isViewMode) {
        <span class="zf-status dot">Read only</span>
        } @else if (hasUnsavedChanges) {
        <span class="zf-status warn dot">Unsaved changes</span>
        } @else {
        <span class="zf-status warn dot">Draft</span>
        }
      </div>
    </div>

    @if (!isViewMode && stockTakeData && stockTakeData.length > 0) {
    <div class="zf-doc-actions">
      <button class="btn btn-secondary" type="button" [disabled]="isSaving" (click)="saveDraft()"
        title="Saves the current changes without finalizing the stock take.">
        <i class="bi bi-save"></i> Save draft
      </button>
      <button class="btn zf-btn-hero" type="button" [disabled]="isSaving || isSubmitting || hasUnsavedChanges"
        (click)="submitDraft()"
        title="Finalizes the stock take and applies changes to inventory. Requires all items to be updated.">
        @if (isSubmitting) { Submitting… } @else { Submit count }
      </button>
    </div>
    }
  </div>

  <!-- ── Audit scope (header card) ────────────────────────────────────── -->
  <div class="zf-doc-section">
    <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
      <span class="zf-section-title">Audit scope</span>
      <div class="zf-sum-chips">
        @if (!isViewMode && !stockTakeData && !isLoadingDraftItems) {
        <!-- The stock take is counted at the store/warehouse selected in the
             header switcher — switch there to count somewhere else. -->
        <span class="zf-sum-chip st-store-chip">
          <span class="k">Location</span>
          <app-warehouse-select (selectionChange)="onWarehouseChange($event)"></app-warehouse-select>
        </span>
        <span class="zf-sum-chip">
          <span class="k">Count type</span>{{ uploadType === 'partial' ? 'Partial' : 'Full' }}
        </span>
        } @else {
        <span class="zf-sum-chip" [class.muted]="!storeContextLabel">
          <span class="k">Location</span>{{ storeContextLabel || 'Not resolved' }}
        </span>
        <span class="zf-sum-chip" [class.muted]="!stockTakeModeLabel">
          <span class="k">Count type</span>{{ stockTakeModeLabel || '—' }}
        </span>
        <span class="zf-sum-chip">
          <span class="k">Lines</span><span class="mono">{{ totalItems }}</span>
        </span>
        @if (stockTakeData?.length) {
        <span class="zf-sum-chip primary">{{ shortLineCount }} short · {{ overLineCount }} over on this page</span>
        }
        }
      </div>
      <span class="zf-section-toggle">
        {{ headerOpen ? 'Collapse header' : (isViewMode ? 'Show header' : 'Edit header') }}
        <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
      </span>
    </div>

    @if (headerOpen) {
    <div class="zf-section-body zf-stack">
      @if (!isViewMode && !stockTakeData && !isLoadingDraftItems) {
      <div class="zf-fgrid" style="--cols: minmax(0,1.4fr) 260px minmax(0,1fr)">
        <div class="zf-field">
          <span class="zf-flabel">Location <span class="req">*</span> <span class="meta">· from the header switcher</span></span>
          <div class="zf-static" [class.text-danger]="!selectedStoreId">
            <i class="bi" [class.bi-shop]="selectedStoreId" [class.bi-exclamation-circle]="!selectedStoreId"></i>
            {{ selectedStoreName ? (selectedStoreName + (selectedStoreCode ? ' (' + selectedStoreCode + ')' : '')) : 'No store/warehouse selected' }}
          </div>
          <small class="zf-field-hint">The uploaded stock take will be created for this store/warehouse.</small>
        </div>

        <ng-container *ngIf="selectedStoreId; else chooseStoreFirst">
          <div class="zf-field">
            <span class="zf-flabel">Count type</span>
            <div class="zf-seg upload-type-selector" role="radiogroup" aria-label="Stock take type">
              <label class="zf-seg-opt" [class.on]="uploadType === 'full'" for="fullStocktakeRadio"
                title="Every SKU at the store is counted; uncounted SKUs are treated as zero">
                <input class="visually-hidden" type="radio" name="uploadTypeRadio" id="fullStocktakeRadio" value="full"
                  [checked]="uploadType === 'full'" (change)="updateUploadType('full')" />
                Full stocktake
              </label>
              <label class="zf-seg-opt" [class.on]="uploadType === 'partial'" for="partialStocktakeRadio"
                title="Only the uploaded SKUs are counted; everything else is left untouched">
                <input class="visually-hidden" type="radio" name="uploadTypeRadio" id="partialStocktakeRadio"
                  value="partial" [checked]="uploadType === 'partial'" (change)="updateUploadType('partial')" />
                Partial stocktake
              </label>
            </div>
          </div>

          <div class="zf-field">
            <span class="zf-flabel">Counting sheet <span class="meta">· XLSX · columns: SKU, counted qty</span></span>
            <app-excel-upload [uploadUrl]="uploadUrl" (uploadSuccess)="onUploadSuccess($event)"
              (uploadError)="onUploadError($event)"></app-excel-upload>
            <small class="zf-field-hint">Uploading creates the draft; add or correct lines below afterwards.</small>
          </div>
        </ng-container>

        <ng-template #chooseStoreFirst>
          <div class="zf-field" style="grid-column: span 2">
            <span class="zf-flabel">Counting sheet</span>
            <div class="zf-alert info">
              <i class="bi bi-info-circle"></i>
              <span>Select the store/warehouse at the top of the screen to upload the stock take file.</span>
            </div>
          </div>
        </ng-template>
      </div>
      } @else {
      <div class="zf-fgrid" style="--cols: minmax(0,1.4fr) repeat(3, minmax(0,1fr))">
        <div class="zf-field">
          <span class="zf-flabel">Location</span>
          <div class="zf-static"><i class="bi bi-shop"></i>{{ storeContextLabel || 'Not resolved' }}</div>
        </div>
        <div class="zf-field">
          <span class="zf-flabel">Count type</span>
          <div class="zf-static"><i class="bi bi-clipboard-check"></i>{{ stockTakeModeLabel ? stockTakeModeLabel + ' stocktake' : '—' }}</div>
        </div>
        <div class="zf-field">
          <span class="zf-flabel">Status</span>
          <div class="zf-static">{{ isViewMode ? 'Read only' : (hasUnsavedChanges ? 'Draft · unsaved changes' : 'Draft') }}</div>
        </div>
        <div class="zf-field">
          <span class="zf-flabel">Lines in draft</span>
          <div class="zf-static zf-mono">{{ totalItems }}</div>
        </div>
      </div>
      }
    </div>
    }
  </div>

  <!-- ── Count lines ───────────────────────────────────────────────────── -->
  <div class="zf-doc-section zf-lines-card">
    <div class="zf-lines-head">
      <h2>Count lines</h2>
      <span class="zf-count">{{ stockLineCountLabel }}</span>
      @if (stockTakeData?.length) {
      <span class="zf-units">{{ pageNewTotal | number:'1.0-3' }} units counted on this page</span>
      <div class="zf-pills">
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'all'" (click)="lineFilter = 'all'">
          All<span class="n">{{ pagedData.length }}</span>
        </button>
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'short'" (click)="lineFilter = 'short'">
          Short<span class="n">{{ shortLineCount }}</span>
        </button>
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'over'" (click)="lineFilter = 'over'">
          Over<span class="n">{{ overLineCount }}</span>
        </button>
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'match'" (click)="lineFilter = 'match'">
          Matching<span class="n">{{ matchLineCount }}</span>
        </button>
        @if (lineFilter !== 'all') {
        <button type="button" class="zf-pill clear" (click)="clearLineFilters()">✕ Clear filter</button>
        }
      </div>
      }

      @if (!isLoadingDraftItems && stockTakeData) {
      <div class="zf-lines-tools">
        <label class="zf-search" style="width:190px">
          <i class="bi bi-search"></i>
          <input #searchInput [(ngModel)]="searchText" (ngModelChange)="onSearchChange()" placeholder="Search SKU or name" />
        </label>

        @if (!isViewMode) {
        <div class="zf-tool" style="width:150px">
          <span class="zf-tool-k mono">SKU</span>
          <ng-select [items]="productSearchResults" bindLabel="sku" [typeahead]="skuSearchSubject"
            [(ngModel)]="selectedBaseProduct" [loading]="isSearchingSku" placeholder="Enter SKU..."
            (change)="onProductSelected($event)" (blur)="clearSearch('sku')" [clearable]="true">
            <ng-template ng-label-tmp let-item="item">
              {{ item.sku }} - {{ item.name }}
            </ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary">{{ item.sku }}</span>
                <span class="ss-row__secondary">{{ item.name }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="zf-tool" style="width:150px">
          <span class="zf-tool-k mono">EAN</span>
          <ng-select [items]="productSearchResults" bindLabel="matchedEan" [typeahead]="eanSearchSubject"
            [(ngModel)]="selectedBaseProduct" [loading]="isSearchingEan" placeholder="Type / scan EAN..."
            (change)="onProductSelected($event)" (blur)="clearSearch('ean')" [clearable]="true">
            <ng-template ng-label-tmp let-item="item">
              {{ item.matchedEan || item.sku }} - {{ item.name }}
            </ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary">{{ item.matchedEan || item.sku }}</span>
                <span class="ss-row__secondary">{{ item.sku }} - {{ item.name }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="zf-tool" style="width:240px">
          <span class="zf-tool-k"><i class="bi bi-plus-lg"></i></span>
          <ng-select [items]="productSearchResults" bindLabel="name" [typeahead]="nameSearchSubject"
            [(ngModel)]="selectedBaseProduct" [loading]="isSearchingName" placeholder="Search & add product"
            (change)="onProductSelected($event)" (blur)="clearSearch('name')" [clearable]="true">
            <ng-template ng-label-tmp let-item="item">
              {{ item.name }} - {{ item.sku }}
            </ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary">{{ item.name }}</span>
                <span class="ss-row__secondary">{{ item.sku }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>
        }
      </div>
      }
    </div>

    <!-- Add-line strip: appears once a product is picked -->
    @if (!isLoadingDraftItems && stockTakeData && !isViewMode && selectedBaseProduct) {
    <div class="zf-add-strip">
      <div class="zf-add-prod">
        <span class="k">Adding</span>
        <span class="n" [title]="selectedBaseProduct.name">{{ selectedBaseProduct.name }}</span>
        <span class="s">
          SKU {{ selectedBaseProduct.sku }}
          @if (selectedVariant?.variantName) { · {{ selectedVariant?.variantName }} }
        </span>
      </div>
      <div class="zf-field">
        <span class="zf-flabel">Counted qty</span>
        <input type="number" class="form-control zf-mono" [(ngModel)]="quantityToAdd" min="1" required
          (keydown.enter)="addProductToDraft()" />
      </div>
      <button class="btn btnprimary" type="button" [disabled]="
          !quantityToAdd || quantityToAdd <= 0 || !selectedBaseProduct
        " (click)="addProductToDraft()">
        <i class="bi bi-plus-lg"></i> Add item
      </button>
      <button type="button" class="btn zf-btn-ghost" (click)="onProductSelected(null)" title="Cancel">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
    }

    @if (!isLoadingDraftItems && stockTakeData && storeContextLabel) {
    <div class="zf-scoped">
      <span class="k">Scoped</span>
      <span>Variance is computed against SOH at <strong>{{ storeContextLabel }}</strong>
        @if (stockTakeModeLabel) { · <strong>{{ stockTakeModeLabel }}</strong> stocktake }
      </span>
      <span class="hint">Pills filter this page · search covers the whole draft</span>
    </div>
    }

    @if (isLoadingDraftItems) {
    <div class="zf-sheet-blank">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <span class="s">Loading draft items...</span>
    </div>
    } @else if (stockTakeData) {
      @if (stockTakeData.length) {
      <div class="zf-sheet-wrap">
        <table class="zf-sheet st-sheet">
          <thead>
            <tr>
              <th class="st" style="--l:0; width:42px">#</th>
              <th class="st" style="--l:42px; width:110px">SKU</th>
              <th class="st st-end" style="--l:152px">Product name</th>
              <th class="num" style="width:130px">Existing qty · SOH</th>
              <th class="num" style="width:110px">New qty</th>
              <th class="num" style="width:100px">Variance</th>
              @if (!isViewMode) {
              <th class="ctr" style="width:120px">Action</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of visibleStockLines; track row.item; let i = $index) {
            <tr [class.is-short]="row.item.varianceQty < 0">
              <td class="st zf-idx" style="--l:0">{{ (currentPage - 1) * itemsPerPage + row.index + 1 }}</td>
              <td class="st" style="--l:42px"><span class="st-sku">{{ row.item.sku }}</span></td>
              <td class="st st-end zf-name" style="--l:152px">
                <span class="n" [title]="row.item.displayName">{{ row.item.displayName }}</span>
                <span class="s" [class.short]="lineState(row.item) === 'short'" [class.over]="lineState(row.item) === 'over'">
                  {{ lineState(row.item) === 'short' ? 'SHORT' : (lineState(row.item) === 'over' ? 'OVER' : 'MATCHES SOH') }}
                </span>
              </td>

              <td class="num">
                <input type="number" class="zf-cell-in st-qty" [(ngModel)]="row.item.existingQty"
                  (ngModelChange)="onQtyChange(row.item)" [disabled]="isViewMode" readonly />
              </td>

              <td class="num">
                <input type="number" class="zf-cell-in st-qty" [id]="'qty-' + row.index" [(ngModel)]="row.item.newQty"
                  (ngModelChange)="onQtyChange(row.item)" (keydown.enter)="updateItem(row.item)" [disabled]="isViewMode" />
              </td>

              <td class="num strong" [class.st-pos]="row.item.varianceQty > 0" [class.st-neg]="row.item.varianceQty < 0">
                {{ row.item.varianceQty > 0 ? '+' : '' }}{{ row.item.varianceQty | number : "1.0-3" }}
              </td>

              @if (!isViewMode) {
              <td class="ctr">
                <button type="button" class="zf-link st-update" [disabled]="isViewMode" (click)="updateItem(row.item)"
                  title="Save this line's counted quantity">
                  Update
                </button>
                <button type="button" class="zf-del" title="Delete line" [disabled]="isViewMode" (click)="deleteRow(row.item)">
                  <i class="bi bi-trash3"></i>
                </button>
              </td>
              }
            </tr>
            }
            @if (!visibleStockLines.length) {
            <tr>
              <td [attr.colspan]="sheetColCount" class="zf-sheet-empty">
                <div>
                  <span class="t">No lines match this filter</span>
                  <span class="s">Clear the filter to see all {{ pagedData.length }} lines on this page.</span>
                </div>
              </td>
            </tr>
            }
          </tbody>
          <tfoot>
            <tr>
              <td class="st" colspan="3" style="--l:0"><span class="k">TOTAL</span>{{ stockLineCountLabel }}</td>
              <td class="num dim">{{ pageSohTotal | number:'1.0-3' }}</td>
              <td class="num">{{ pageNewTotal | number:'1.0-3' }}</td>
              <td class="num strong">{{ pageVarianceTotal > 0 ? '+' : '' }}{{ pageVarianceTotal | number:'1.0-3' }}</td>
              @if (!isViewMode) {
              <td></td>
              }
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="st-foot">
        <div class="st-foot-size">
          <select class="form-select" [(ngModel)]="itemsPerPage" (change)="onItemsPerPageChange()">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="30">30</option>
            <option value="50">50</option>
          </select>
          <span>entries per page</span>
        </div>

        <span class="st-foot-show">
          Showing {{ pagedData.length }} items out of {{ totalItems }} total
        </span>

        <nav aria-label="Table Pagination">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" [class.disabled]="currentPage === 1">
              <a class="page-link" (click)="changePage(currentPage - 1)">&laquo;</a>
            </li>

            <ng-container *ngFor="let i of [].constructor(totalPages); let p = index">
              <li class="page-item" [class.active]="p + 1 === currentPage" *ngIf="
                  p + 1 === 1 ||
                  p + 1 === totalPages ||
                  (p + 1 >= startPage && p + 1 <= endPage)
                " (click)="changePage(p + 1)">
                <a class="page-link">{{ p + 1 }}</a>
              </li>

              <li class="page-item disabled" *ngIf="p + 1 === 2 && startPage > 2">
                <span class="page-link">...</span>
              </li>

              <li class="page-item disabled" *ngIf="p + 1 === totalPages - 1 && endPage < totalPages - 1">
                <span class="page-link">...</span>
              </li>
            </ng-container>

            <li class="page-item" [class.disabled]="currentPage === totalPages || totalPages === 0">
              <a class="page-link" (click)="changePage(currentPage + 1)">&raquo;</a>
            </li>
          </ul>
        </nav>
      </div>
      } @else {
      <div class="zf-sheet-blank">
        @if (searchText) {
        <span class="t">No lines match “{{ searchText }}”</span>
        <span class="s">Clear the search to see all {{ totalItems }} lines of this draft.</span>
        } @else {
        <span class="t">This stock take draft has no items.</span>
        <span class="s">
          @if (isViewMode) { Nothing was counted on this draft. }
          @else { Scan a SKU or EAN, or search a product above to add it to the count. }
        </span>
        }
      </div>
      }
    } @else {
    <div class="zf-sheet-blank">
      <span class="t">Nothing counted on this draft yet</span>
      <span class="s">Pick the count type and upload the counting sheet above — the draft is created from it.</span>
    </div>
    }
  </div>

  <!-- ── Variance strip ────────────────────────────────────────────────── -->
  @if (!isLoadingDraftItems && stockTakeData) {
  <div class="zf-order-strip">
    <div class="zf-strip-info">
      <div class="zf-strip-line">
        <span class="dot" [class.ok]="stockTakeData.length && !hasUnsavedChanges"></span>
        {{ totalItems }} {{ totalItems === 1 ? 'line' : 'lines' }} in this draft · {{ pagedData.length }} on this page
      </div>
      <div class="zf-strip-sub">
        @if (hasUnsavedChanges) {
        <span>Quantities edited — press <strong>Update</strong> on each changed row before submitting.</span>
        } @else if (!stockTakeData.length) {
        <span>Add a product or upload a counting sheet to get lines on this draft.</span>
        } @else {
        <span>All quantity changes are saved · variance figures cover the lines on this page</span>
        }
      </div>
    </div>
    <div class="zf-strip-kpis">
      <div class="zf-kpi">
        <span class="k">Short</span>
        <span class="v" [class.st-neg]="shortLineCount > 0">{{ shortLineCount }}</span>
      </div>
      <div class="zf-kpi">
        <span class="k">Over</span>
        <span class="v">{{ overLineCount }}</span>
      </div>
      <div class="zf-kpi green">
        <span class="k">Matching</span>
        <span class="v">{{ matchLineCount }}</span>
      </div>
      <div class="zf-kpi dark">
        <span class="k">Net variance · this page</span>
        <span class="v">{{ pageVarianceTotal > 0 ? '+' : '' }}{{ pageVarianceTotal | number:'1.0-3' }} units</span>
      </div>
    </div>
  </div>
  }
</div>
